import { useState } from 'react';
import { LOGIN_DEFAULT_ERROR_MESSAGE } from '../../constants/data';
import { ErrorData } from './ErrorData';

interface ExtractMessage<T> {
  (data: T): ErrorData | undefined;
}

/**
 * @param defaultMessage - mensaje de error genérico a
 * mostrar en caso de que se marque error pero sin
 * especificar mensaje.
 * @param extractMessage - función para extraer el mensaje
 * de error en caso de que se reciba un objeto en la función
 * "setMessage".
 * @returns errorMessage - mensaje de error a mostrar
 * al usuario.
 * @returns setMessage - función para marcar que se debe
 * mostrar un mensaje de error. Internamente analiza si
 * obtiene el mensaje de los parámetros que recibe o si
 * utiliza el mensaje establecido por defecto.
 * @returns show - flag que indica si hay mensaje para
 * mostrar.
 * @returns clear - se indica que se debe dejar de mostrar
 * el mensaje de error.
 */
export const useErrorMessage = <T>(extractMessage: ExtractMessage<T>, defaultMessage: string = LOGIN_DEFAULT_ERROR_MESSAGE) => {
  const [errorData, setErrorData] = useState<ErrorData | undefined>();
  const [show, setShow] = useState<boolean>(false);

  const setMessage = (msg?: string | undefined | T) => {
    const defaultErrorData: ErrorData = { message: defaultMessage };

    if (msg == null) {
      setErrorData(defaultErrorData);
    } else if (typeof msg === 'string') {
      if (msg.length > 0) setErrorData({ message: msg });
      else setErrorData(defaultErrorData);
    } else if (extractMessage != null) {
      const extractedErrorData = extractMessage(msg);

      if (extractedErrorData) {
        if (extractedErrorData.message) setErrorData(extractedErrorData);
        else setErrorData({ ...defaultErrorData, code: extractedErrorData.code });
      } else setErrorData(defaultErrorData);
    } else {
      setErrorData(defaultErrorData);
    }

    setShow(true);
  };

  const clear = () => {
    setShow(false);
    setErrorData(undefined);
  };

  return [errorData, setMessage, show, clear] as const;
};
